/**
 * MomentsLandingPage — public sales page for HustleMoments.
 *
 * Positioning: HustleMoments is the Memogram-equivalent add-on inside
 * HustleSuite. Business owners activate it from their membership tier;
 * no separate purchase, no in-app buys, no App Store fees.
 *
 * iter253: Converged onto shared PlatformHeader + SuiteFooter chrome
 * so hustlemoments.* visually lines up with every other module. Root
 * gets a dark:* variant so the theme toggle in the black top-bar
 * flips the page correctly.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Camera, QrCode, Users, Video, Star, ShieldCheck,
  Smartphone, Radio, Wallet, Share2, PlayCircle,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

export default function MomentsLandingPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = t("moments_meta_title", {
      defaultValue: "HustleMoments — Turn every customer into your camera crew",
    });
  }, [i18n.language, t]);

  const NAV = [
    { to: "/",                  label: t("moments_nav_home",      { defaultValue: "Home" }) },
    { to: "/moments/dashboard", label: t("moments_nav_dashboard", { defaultValue: "Dashboard" }), align: "right", variant: "primary" },
  ];
  const bullets = [
    { icon: Camera,    text: "Guests upload straight from their phone camera — no app to install." },
    { icon: Video,     text: "Photos AND videos, up to 25 MB per file, HD download included." },
    { icon: Star,      text: "Approve, reject or auto-publish. You own every asset." },
    { icon: ShieldCheck, text: "Content moderation queue with per-item reject / delete." },
    { icon: Radio,     text: "Web NFC — tap a sticker to open, no scanning." },
    { icon: Wallet,    text: "Save the QR to Apple/Google Wallet for staff & concierge." },
  ];

  const useCases = [
    ["Restaurants & cafés",  "Table QR → dish photos & short reviews land in your gallery."],
    ["Weddings",             "Every guest becomes a photographer — hundreds of candid moments."],
    ["Hotels & resorts",     "Room welcome cards with a QR — guests share their stay."],
    ["Events & conferences", "Real-time UGC feed to a live TV slideshow."],
    ["Gyms & studios",       "Transformation & class photos, moderated to your standard."],
    ["Retail & pop-ups",     "Unboxings + product try-ons uploaded on the spot."],
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-stone-50 to-white text-stone-900 dark:from-[#0a0908] dark:to-stone-950 dark:text-stone-100 transition-colors"
      data-testid="moments-landing-root"
    >
      <PlatformHeader brand={{ name: "HustleMoments", to: "/" }} nav={NAV} showBookCta={false} showCart={false} />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold tracking-widest text-pink-600 uppercase">
              New HustleSuite module
            </p>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">
              Turn every customer into<br/>your camera crew.
            </h1>
            <p className="mt-5 text-lg text-stone-700 max-w-lg dark:text-stone-200">
              Print one QR code. Guests scan, upload photos, videos and
              reviews from their phone &mdash; instantly, no app, no login. You
              get a moderated gallery of real customer content for
              marketing, socials and word of mouth.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/moments/dashboard"
                data-testid="moments-landing-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-pink-600 hover:bg-pink-500 text-white px-6 h-12 text-[15px] font-semibold shadow-lg shadow-pink-600/25"
              >
                Open my Moments dashboard
              </Link>
              <a
                href="#how"
                data-testid="moments-landing-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-stone-300 hover:border-stone-500 px-5 h-12 text-[15px] font-semibold"
              >
                <PlayCircle size={16}/> See it in 60 seconds
              </a>
            </div>
            <p className="mt-4 text-xs text-stone-500">
              Included with your HustleSuite membership. Billing rides
              your existing Stripe subscription — no per-page fees.
            </p>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="rounded-3xl bg-gradient-to-br from-pink-500 via-rose-500 to-amber-400 p-8 shadow-2xl shadow-pink-500/20">
              <div className="bg-white rounded-2xl p-6 space-y-4 dark:bg-stone-900">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                    <QrCode className="text-pink-600" size={22}/>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 uppercase font-bold">Live gallery</p>
                    <p className="font-black text-stone-900 dark:text-stone-100">Sarah&apos;s 40th Birthday</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[1,2,3,4,5,6].map((i) => (
                    <div key={i}
                         className="aspect-square rounded-xl bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                      <Camera size={20} className="text-stone-400"/>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-500 pt-2 border-t border-stone-100">
                  <Users size={13}/> 142 uploads this event
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bullets */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-black mb-8">
          Everything Memogram promises — inside your Suite.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {bullets.map(({ icon: Icon, text }, i) => (
            <div key={i} className="rounded-2xl border border-stone-200 p-5 hover:shadow-lg transition dark:border-stone-700"
                 data-testid={`moments-landing-bullet-${i}`}>
              <Icon className="text-pink-600 mb-3" size={22}/>
              <p className="text-sm text-stone-800 leading-relaxed dark:text-stone-100">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3 steps */}
      <section className="bg-stone-950 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-black mb-10">In 3 steps.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["Create your Moment Page", "Name, event date, cover image, theme — under 60 seconds."],
              ["Print the QR",            "Download PNG or SVG. Tent cards, posters, invitations, wristbands."],
              ["Own the gallery",         "Guests upload from their phone. You approve, download HD, repost."],
            ].map(([title, blurb], i) => (
              <div key={i} className="rounded-2xl bg-stone-900/60 p-6 border border-white/10">
                <div className="text-5xl font-black text-pink-500 mb-3">{i + 1}</div>
                <h3 className="text-lg font-bold mb-2">{title}</h3>
                <p className="text-sm text-stone-300">{blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-black mb-8">Where it earns its keep.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {useCases.map(([t, b], i) => (
            <div key={i} className="rounded-xl bg-stone-50 border border-stone-200 p-5 dark:bg-stone-900 dark:border-stone-700"
                 data-testid={`moments-usecase-${i}`}>
              <h3 className="font-bold text-stone-900 mb-2 dark:text-stone-100">{t}</h3>
              <p className="text-sm text-stone-600 dark:text-stone-400">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Native features */}
      <section className="bg-gradient-to-b from-white to-stone-100 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-2 text-xs uppercase font-bold tracking-widest text-stone-500 mb-2">
            <Smartphone size={14}/> Native phone superpowers
          </div>
          <h2 className="text-2xl sm:text-3xl font-black mb-8">
            Every phone feature you already own — put to work.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              [Camera,     "Native camera",   "Direct rear-camera capture, HD, no app."],
              [Radio,      "Tap-to-open NFC", "Android NFC stickers replace scanning."],
              [Wallet,     "Wallet passes",   "Save the QR to Apple / Google Wallet."],
              [Share2,     "Share sheet",     "Native share to WhatsApp, iMessage, socials."],
            ].map(([Icon, t, b], i) => (
              <div key={i} className="rounded-2xl bg-white border border-stone-200 p-5 dark:bg-stone-900 dark:border-stone-700">
                <Icon className="text-pink-600 mb-3" size={22}/>
                <h3 className="font-bold text-stone-900 mb-1 dark:text-stone-100">{t}</h3>
                <p className="text-xs text-stone-600 leading-relaxed dark:text-stone-400">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl sm:text-4xl font-black">
          Ready to collect moments?
        </h2>
        <p className="mt-4 text-lg text-stone-600 dark:text-stone-400">
          Every HustleSuite plan includes Moments. No extra checkout, no
          per-page fees — just create your first page and print the QR.
        </p>
        <Link
          to="/moments/dashboard"
          data-testid="moments-landing-final-cta"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-pink-600 hover:bg-pink-500 text-white px-8 h-14 text-lg font-bold shadow-xl shadow-pink-600/25"
        >
          Create your first Moment Page →
        </Link>
      </section>
      <SuiteFooter />
    </div>
  );
}
